/**
 * localStorage/sessionStorage 安全封装 + TTL + 内存缓存
 */

/** 存储类型 */
export type StorageType = 'local' | 'session';

/** 存储选项 */
export interface StorageOptions {
  /** 存储类型，默认 'local' */
  storageType?: StorageType;
  /** 过期时间 (毫秒)，不设置则永不过期 */
  ttl?: number;
}

/** 存储条目元数据 */
interface StorageEntry<T> {
  value: T;
  /** 过期时间戳 */
  expiresAt?: number;
  /** 写入时间戳 */
  createdAt: number;
}

/**
 * 获取存储实例
 */
function getStorage(type: StorageType = 'local'): Storage | null {
  try {
    return type === 'session' ? sessionStorage : localStorage;
  } catch {
    // 无痕模式或 SSR 环境下 Storage 不可用
    return null;
  }
}

/**
 * 安全读取存储数据
 *
 * @param key - 存储 key
 * @param storageType - 存储类型，默认 'local'
 * @returns 解析后的数据或 null
 *
 * @example
 * ```ts
 * const user = safeGetStorage<User>('currentUser');
 * const token = safeGetStorage<string>('token');
 * ```
 */
export function safeGetStorage<T = unknown>(
  key: string,
  storageType: StorageType = 'local',
): T | null {
  const storage = getStorage(storageType);
  if (!storage) return null;

  try {
    const raw = storage.getItem(key);
    if (raw === null) return null;

    // 尝试解析为 JSON
    const parsed = JSON.parse(raw);

    // 检查是否为 StorageEntry 包装格式（双重验证 value + createdAt，避免误匹配普通对象）
    if (
      parsed &&
      typeof parsed === 'object' &&
      'value' in parsed &&
      'createdAt' in parsed
    ) {
      const entry = parsed as StorageEntry<T>;
      // 检查 TTL 过期
      if (entry.expiresAt && Date.now() > entry.expiresAt) {
        storage.removeItem(key);
        return null;
      }
      return entry.value;
    }

    // 支持非包装格式 (直接存储的 JSON 值)
    return parsed as T;
  } catch {
    // JSON 解析失败: 原始值不是有效 JSON，无法安全转换为 T
    return null;
  }
}

/**
 * 安全写入存储数据
 *
 * @param key - 存储 key
 * @param value - 要存储的数据
 * @param options - 存储选项 (TTL 等)
 * @returns 是否写入成功
 *
 * @example
 * ```ts
 * safeSetStorage('user', { id: 1, name: 'test' });
 * safeSetStorage('token', 'abc123', { ttl: 7 * 24 * 60 * 60 * 1000 }); // 7天过期
 * safeSetStorage('cache', data, { storageType: 'session' });
 * ```
 */
export function safeSetStorage(
  key: string,
  value: unknown,
  options?: StorageOptions,
): boolean {
  const storage = getStorage(options?.storageType);
  if (!storage) return false;

  try {
    const entry: StorageEntry<unknown> = {
      value,
      createdAt: Date.now(),
    };

    if (options?.ttl && options.ttl > 0) {
      entry.expiresAt = Date.now() + options.ttl;
    }

    storage.setItem(key, JSON.stringify(entry));
    return true;
  } catch {
    // 存储空间已满等错误，静默失败
    return false;
  }
}

/**
 * 安全删除存储数据
 *
 * @param key - 存储 key
 * @param storageType - 存储类型，默认 'local'
 */
export function safeRemoveStorage(
  key: string,
  storageType: StorageType = 'local',
): void {
  const storage = getStorage(storageType);
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch {
    // Storage 操作偶发失败（无痕模式等），静默处理
  }
}

/**
 * 清空指定类型的存储
 *
 * @param storageType - 存储类型，默认 'local'
 */
export function clearStorage(storageType: StorageType = 'local'): void {
  const storage = getStorage(storageType);
  if (!storage) return;

  try {
    storage.clear();
  } catch {
    // Storage 操作偶发失败（无痕模式等），静默处理
  }
}

/**
 * 检查存储项是否已过期
 *
 * @param key - 存储 key
 * @param storageType - 存储类型，默认 'local'
 * @returns 是否已过期 (不存在也返回 true)
 */
export function isStorageExpired(
  key: string,
  storageType: StorageType = 'local',
): boolean {
  const storage = getStorage(storageType);
  if (!storage) return true;

  try {
    const raw = storage.getItem(key);
    if (raw === null) return true;

    const parsed = JSON.parse(raw) as StorageEntry<unknown>;

    // 检查是否受管: 需要有 value 且 createdAt
    if (
      parsed &&
      typeof parsed === 'object' &&
      'value' in parsed &&
      'createdAt' in parsed
    ) {
      if ('expiresAt' in parsed && parsed.expiresAt != null) {
        return Date.now() > parsed.expiresAt;
      }
      return false; // 受管但无过期设置则不过期
    }
    return false; // 非受管格式，不过期
  } catch {
    // JSON 解析失败说明非受管格式，不过期
    return false;
  }
}

/**
 * 清理所有已过期的存储项
 *
 * @param storageType - 存储类型，默认 'local'
 * @returns 清理的条数
 */
export function cleanExpiredStorage(
  storageType: StorageType = 'local',
): number {
  const storage = getStorage(storageType);
  if (!storage) return 0;

  let cleaned = 0;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && isStorageExpired(key, storageType)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      storage.removeItem(key);
      cleaned++;
    }
  } catch {
    // Storage 遍历或删除偶发失败，返回已清理数量
  }
  return cleaned;
}

/**
 * 获取存储空间大小 (估算，单位字节)
 *
 * @param storageType - 存储类型，默认 'local'
 * @returns 估算大小 (字节)
 */
export function getStorageSize(storageType: StorageType = 'local'): number {
  const storage = getStorage(storageType);
  if (!storage) return 0;

  let size = 0;
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key) {
        const value = storage.getItem(key);
        size += key.length + (value?.length ?? 0);
      }
    }
  } catch {
    // Storage 遍历偶发失败，返回已累积的大小
  }
  // UTF-16 编码，每个字符 2 字节
  return size * 2;
}

// ==================== 内存缓存 ====================

/**
 * 内存缓存配置
 */
export interface MemoryCacheOptions {
  /** 最大缓存条数，默认 100 */
  maxSize?: number;
  /** 默认过期时间 (毫秒)，默认不过期 */
  defaultTtl?: number;
}

/**
 * 内存缓存实例
 */
export interface MemoryCache {
  /** 获取缓存值 */
  get<T = unknown>(key: string): T | undefined;
  /** 设置缓存值 */
  set(key: string, value: unknown, ttl?: number): void;
  /** 删除缓存值 */
  delete(key: string): boolean;
  /** 检查 key 是否存在 (且未过期) */
  has(key: string): boolean;
  /** 清空所有缓存 */
  clear(): void;
  /** 获取当前缓存大小 */
  readonly size: number;
}

/**
 * 创建内存缓存实例
 *
 * @param options - 缓存配置
 * @returns 缓存实例
 *
 * @example
 * ```ts
 * const cache = createMemoryCache({ maxSize: 50, defaultTtl: 60000 });
 * cache.set('key', { data: 'value' });
 * const value = cache.get<{ data: string }>('key');
 * ```
 */
export function createMemoryCache(options?: MemoryCacheOptions): MemoryCache {
  const maxSize = options?.maxSize ?? 100;
  const defaultTtl = options?.defaultTtl;
  const store = new Map<string, { value: unknown; expiresAt?: number }>();

  function evict() {
    while (store.size > maxSize) {
      const firstKey = store.keys().next().value;
      if (firstKey === undefined) break;
      store.delete(firstKey);
    }
  }

  return {
    /**
     * 获取缓存值
     */
    get<T = unknown>(key: string): T | undefined {
      const entry = store.get(key);
      if (!entry) return undefined;
      if (entry.expiresAt && Date.now() > entry.expiresAt) {
        store.delete(key);
        return undefined;
      }
      return entry.value as T;
    },

    /**
     * 设置缓存值
     */
    set(key: string, value: unknown, ttl?: number): void {
      const effectiveTtl = ttl ?? defaultTtl;
      const expiresAt =
        effectiveTtl != null && effectiveTtl > 0
          ? Date.now() + effectiveTtl
          : undefined;
      store.set(key, { value, expiresAt });
      evict();
    },

    /**
     * 删除缓存值
     */
    delete(key: string): boolean {
      return store.delete(key);
    },

    /**
     * 检查 key 是否存在 (且未过期)
     */
    has(key: string): boolean {
      const entry = store.get(key);
      if (!entry) return false;
      if (entry.expiresAt && Date.now() > entry.expiresAt) {
        store.delete(key);
        return false;
      }
      return true;
    },

    /**
     * 清空所有缓存
     */
    clear(): void {
      store.clear();
    },

    /**
     * 获取当前缓存大小
     */
    get size(): number {
      return store.size;
    },
  };
}
